/**
 * Chamadas à API real do painel (`apps/server/src/panel.ts`), no mesmo domínio do painel: o nginx
 * manda `/api` para o servidor e a sessão vai no cookie HttpOnly (`credentials: 'same-origin'`).
 * Erros viram `ProviderError` (o formato que as telas já tratam): 401 → `forbidden` com
 * `unauthorized: true`, 404 → `not_found`, 429 → `rate_limited`, rede e 5xx → `source_unavailable`.
 */
import type { ProviderError } from '@rota/contracts';

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export interface HttpOptions {
  /** `fetch` usado (testes); padrão: o do navegador. */
  fetch?: FetchLike;
  /** Prefixo das rotas (padrão: `/api`). */
  base?: string;
}

export interface ApiError extends ProviderError {
  status?: number;
  /** A sessão acabou (401): a interface leva ao `/entrar`. */
  unauthorized?: boolean;
  /** Erros por campo (400), quando o servidor manda `{ error, fields: { "caminho": "frase" } }`. */
  fields?: Record<string, string>;
  /** Lista de erros em frases (400), quando o servidor manda `{ error, errors: ["…", "…"] }` (Palco). */
  details?: string[];
}

export interface SendInit {
  /** Corpo em JSON (vira `content-type: application/json`). */
  json?: unknown;
  /** Corpo cru (pedaço de arquivo, imagem). */
  body?: BodyInit;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export interface Http {
  get<T>(path: string, query?: Record<string, string | number | null | undefined>): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  /** Resposta crua (o login precisa distinguir 401 de 429 sem lançar). */
  raw(path: string, init?: RequestInit): Promise<Response>;
  /** PUT, PATCH, DELETE (e POST com corpo cru): JSON de volta, ou `undefined` em 204/corpo vazio. */
  send<T>(method: 'POST' | 'PUT' | 'PATCH' | 'DELETE', path: string, init?: SendInit): Promise<T>;
}

function codeFor(status: number): ProviderError['code'] {
  if (status === 401 || status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 429) return 'rate_limited';
  if (status === 400) return 'invalid';
  return 'source_unavailable';
}

const OFFLINE = 'O servidor da automação não respondeu. Tentando de novo em instantes.';
const MESSAGE: Partial<Record<ProviderError['code'], string>> = {
  forbidden: 'Sua sessão terminou. Entre de novo.',
  not_found: 'Não encontrado.',
  rate_limited: 'Muitas tentativas. Espere alguns minutos.',
  source_unavailable: OFFLINE,
};

/** Mensagem do servidor (`{ error }`) quando houver; senão, a padrão do código. */
async function errorFrom(res: Response): Promise<ApiError> {
  const code = codeFor(res.status);
  let message = MESSAGE[code] ?? `Erro ${res.status}.`;
  try {
    const body = (await res.json()) as { error?: unknown; fields?: unknown; errors?: unknown };
    if (typeof body.error === 'string' && body.error && res.status < 500 && res.status !== 401)
      message = body.error;
    const details = Array.isArray(body.errors)
      ? body.errors.filter((x): x is string => typeof x === 'string' && x.length > 0)
      : [];
    if (res.status === 400 && body.fields && typeof body.fields === 'object') {
      const fields: Record<string, string> = {};
      for (const [k, v] of Object.entries(body.fields)) if (typeof v === 'string') fields[k] = v;
      return { code, message, status: res.status, fields, ...(details.length ? { details } : {}) };
    }
    if (details.length && res.status < 500 && res.status !== 401)
      return { code, message, status: res.status, details };
  } catch {
    // corpo vazio ou não-JSON: fica a mensagem padrão
  }
  return { code, message, status: res.status, ...(res.status === 401 ? { unauthorized: true } : {}) };
}

export function createHttp(o: HttpOptions = {}): Http {
  const base = o.base ?? '/api';
  const doFetch: FetchLike = o.fetch ?? ((input, init) => fetch(input, init));

  const raw = async (path: string, init: RequestInit = {}): Promise<Response> => {
    try {
      return await doFetch(`${base}${path}`, {
        credentials: 'same-origin',
        ...init,
        headers: { accept: 'application/json', ...(init.headers ?? {}) },
      });
    } catch {
      throw {
        code: 'source_unavailable',
        message: OFFLINE,
      } satisfies ApiError;
    }
  };

  const json = async <T>(res: Response): Promise<T> => {
    if (!res.ok) throw await errorFrom(res);
    return (await res.json()) as T;
  };

  return {
    raw,
    async send<T>(method: 'POST' | 'PUT' | 'PATCH' | 'DELETE', path: string, init: SendInit = {}) {
      const headers: Record<string, string> = { ...(init.headers ?? {}) };
      let body: BodyInit | undefined = init.body;
      if (init.json !== undefined) {
        headers['content-type'] = 'application/json';
        body = JSON.stringify(init.json);
      }
      const res = await raw(path, {
        method,
        headers,
        ...(body !== undefined ? { body } : {}),
        ...(init.signal ? { signal: init.signal } : {}),
      });
      if (!res.ok) throw await errorFrom(res);
      const text = res.status === 204 ? '' : await res.text();
      return (text ? JSON.parse(text) : undefined) as T;
    },
    async get<T>(path: string, query?: Record<string, string | number | null | undefined>) {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(query ?? {})) if (v != null && v !== '') qs.set(k, String(v));
      const s = qs.toString();
      return json<T>(await raw(s ? `${path}?${s}` : path));
    },
    async post<T>(path: string, body?: unknown) {
      return json<T>(
        await raw(path, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body ?? {}),
        }),
      );
    },
  };
}
